import React, { useState } from 'react';
import { useSelector } from 'react-redux';
import {
  Modal,
  ModalHeader,
  ModalBody,
  PageLevelNotification,
} from '@americanexpress/dls-react';
import { Link } from '@americanexpress/one-app-router';
import { useCommonContent } from '@/hooks/oneCmsIntlHooks/oneCmsIntlHelperHooks';
import { IconCheck } from '@americanexpress/dls-icons';
import { cardArtMapping } from '@/constants/productMapping';
import {
  getCardArtUrl,
  getBasePathFromLocation,
  resizeSelectElementToCurrentValue,
  cn,
} from '@/helpers/utils';
import { getLocaleData } from '@/selectors/localeSelectors';
import { getIsLoggedIn } from '@/selectors/authSelectors';
import { getWalletError } from '@/selectors/walletSelector';
import { getOneCmsLoungeFinderImagesBaseUrl } from '@/selectors/oneCmsSelectors';
import { useEnabledMarkets } from '@/hooks/useEnabledMarkets';
import { useResizeSelectElement } from '@/hooks/useResizeSelectElement';
import { useUserCardsWithLoungeAccess } from '@/hooks/memberHooks';
import { useRouteParams } from '@/context/routeParamsContext';
import { useAnalyticsContext } from '@/context/analyticsContext';
import { useModal } from '@/context/modalContext';
import { getCardPathname } from '@/helpers/loungeHelper';
import { useLoungeById } from '@/hooks/loungesHooks/loungesHelperHooks';
import { removeFiltersQuery } from '@/helpers/airportFiltersHelper';
import LoginCard from '../LoginCard';
import Wallet from '../Wallet';
import styles from '../PageLevelNotificationOverride.module.scss';

const SelectCardModal = () => {
  const baseOneCmsImgUrl = useSelector(getOneCmsLoungeFinderImagesBaseUrl);
  const { locale } = useSelector(getLocaleData);
  const isLoggedIn = useSelector(getIsLoggedIn);
  const [selectedLocale, setSelectedLocale] = useState(
    locale ? locale.toLocaleLowerCase() : 'en-us'
  );
  const {
    product: productFromParams,
    airportCode,
    loungeId,
    pathname,
  } = useRouteParams();
  const { customTrack } = useAnalyticsContext();
  const { closeModal, modalName } = useModal();
  const { selectCard, productDisplayNames, login } = useCommonContent();
  const wallet = useUserCardsWithLoungeAccess();
  const walletError = useSelector(getWalletError);
  const lounge = useLoungeById(loungeId);
  const [isChangeEvent, setIsChangeEvent] = useState(false);

  const isOpen = modalName !== '';
  useResizeSelectElement('#market-select', isOpen);

  const basePath = getBasePathFromLocation(selectedLocale);

  const enabledMarkets = useEnabledMarkets();

  const handleSelectMarket = (event) => {
    const newLocale = event.target.value;
    setIsChangeEvent(true);
    setSelectedLocale(newLocale);
    resizeSelectElementToCurrentValue(event.target);

    // track as a click event since adobe does not track change events
    const updatedEvent = event;
    updatedEvent.type = 'click';
    customTrack({
      event: updatedEvent,
      detail: ` ${locale?.toLowerCase()} -> ${newLocale.toLowerCase()}`,
      element: `change region`,
    });
  };

  const handleSelectCard = (event, newProduct, newLocale) => {
    closeModal();
    customTrack({
      event,
      detail: `${productFromParams} ${locale?.toLowerCase()} -> ${newProduct} ${newLocale.toLowerCase()}`,
      element: 'card selector card switch',
    });
  };

  const handleSelectClick = (event) => {
    // prevent double tracking on change event (select onClick also fires on onChange event)
    if (isChangeEvent) {
      setIsChangeEvent(false);
      return;
    }
    customTrack({
      event,
      element: 'region selector',
    });
  };

  const renderMycaExperience = () => {
    if (!isLoggedIn) {
      return <LoginCard label={login?.label} showDivider={true} />;
    }

    if (walletError) {
      return (
        <>
          <PageLevelNotification
            isDismissible={false}
            type="caution"
            className={cn('tw-mb-6', styles.pageLevelNotification)}
          >
            <p className="-tw-ml-2 tw-pr-4 tw-text-base">
              {selectCard?.walletError}
            </p>
          </PageLevelNotification>
          <hr className="tw-mb-6 tw-border-gray-300" />
        </>
      );
    }

    return <Wallet wallet={wallet} handleClick={handleSelectCard} />;
  };

  return (
    <Modal
      id="select-card-modal"
      onClose={(e) => closeModal(e, 'close card selector')}
      innerContainerClassName="tw-max-w-screen-sm md_tw-max-w-screen-md tw-w-full tw-m-0 sm_tw-m-4"
    >
      <ModalHeader
        id="select-card-modal-header"
        closeButtonProps={{ reactlytics: { disable: true } }}
      >
        <h3>{selectCard?.header}</h3>
      </ModalHeader>
      <ModalBody
        className="tw-min-h-75dvh tw-max-h-75vh-important sm_tw-min-h-full"
        style={{ paddingInline: '1rem' }}
      >
        {renderMycaExperience()}
        <div className="sm_tw-flex sm_tw-items-center">
          <label
            htmlFor="market-select"
            className="tw-mb-2 tw-mr-1 tw-inline tw-font-sans tw-text-base tw-font-bold tw-text-gray-700"
          >
            {selectCard?.selectLabel}{' '}
          </label>
          <select
            name="market-select"
            id="market-select"
            aria-label="Select a market"
            className="tw-mr-1 tw-appearance-none tw-bg-transparent tw-bg-icon-chevron-down tw-bg-right-top-1dot25 tw-bg-no-repeat tw-pb-1dot5 tw-font-sans tw-font-bold tw-text-blue-bright-shade-600-s8"
            value={selectedLocale.toLocaleLowerCase()}
            onChange={handleSelectMarket}
            onClick={handleSelectClick}
          >
            {enabledMarkets
              .filter((market) => market.showInCardPicker)
              .map(({ locale: marketLocale, name, multiLanguage }) => {
                const langTag = marketLocale.split('-')[0];
                const displayName = multiLanguage
                  ? `${name} - ${langTag.toUpperCase()}`
                  : name;
                return (
                  <option key={marketLocale} value={marketLocale}>
                    {displayName}
                  </option>
                );
              })}
          </select>
        </div>
        <div
          className="tw-flex tw-flex-col"
          role="group"
          aria-labelledby="card-links-label"
        >
          <p id="card-links-label" className="sr-only">
            Select a card option
          </p>
          {enabledMarkets
            .find(
              (market) => market.locale === selectedLocale.toLocaleLowerCase()
            )
            .products.filter((product) => product.showInCardPicker)
            .map(({ name }) => {
              const productDisplayName =
                productDisplayNames[name]?.selectCardDisplayName;
              const isSelectedCard =
                name === productFromParams &&
                selectedLocale.toLocaleLowerCase() ===
                  locale.toLocaleLowerCase();
              const imgSrc = getCardArtUrl({
                baseOneCmsImgUrl,
                product: cardArtMapping[name],
                locale: selectedLocale,
              });
              const cardInWallet = wallet?.some(
                (card) =>
                  card.loungeFinderProduct === name &&
                  card.homeCountryLocale.toLocaleLowerCase() ===
                    selectedLocale.toLocaleLowerCase()
              );

              // do not show the card if the user has it in their wallet
              if (cardInWallet) {
                return null;
              }
              return (
                <Link
                  key={name}
                  to={{
                    pathname: getCardPathname({
                      basePath,
                      product: name,
                      airportCode,
                      lounge,
                      locale: selectedLocale,
                      pathname,
                    }),
                    search: removeFiltersQuery(),
                  }}
                  className={cn(
                    'tw-flex tw-items-center tw-gap-4 tw-px-2 tw-py-4 hover_tw-rounded-lg hover_tw-bg-gray-200',
                    {
                      'tw-rounded-lg tw-bg-gray-200': isSelectedCard,
                    }
                  )}
                  onClick={(e) => {
                    const localeChanged =
                      selectedLocale !== locale.toLocaleLowerCase();
                    if (localeChanged && name !== 'the-centurion-card') {
                      // If the locale changes, prevent client-side navigation.
                      // This ensures consent and cookie logic runs fresh
                      // in the new locale. Otherwise, cookies/tracking from
                      // the old locale may persist and can't be reliably
                      // disabled. The centurion card is an exception because
                      // it always navigates to the login page, which runs a
                      // full page reload.
                      e.preventDefault(); // prevent client-side navigation
                      handleSelectCard(e, name, selectedLocale); // track the click before navigating to the new locale
                      const url = e.currentTarget.href;
                      setTimeout(() => {
                        // Use setTimeout to delay window.location.assign until after the event loop, ensuring tracking and modal close logic complete before navigation.
                        window.location.assign(url);
                      }, 0);
                    } else {
                      handleSelectCard(e, name, selectedLocale); // track the click and allow client-side navigation when the locale didn't change
                    }
                  }}
                  aria-current={isSelectedCard ? 'true' : undefined}
                >
                  <img
                    src={imgSrc}
                    alt="card-art"
                    className="tw-h-11 tw-w-17dot5"
                  />
                  <div className="tw-flex tw-flex-col">
                    <p className="tw-text-gray-800">{productDisplayName}</p>
                    {name === 'the-centurion-card' && (
                      <p className="tw-text-sm tw-text-gray-700">
                        {selectCard?.centurionMessage}
                      </p>
                    )}
                  </div>
                  {isSelectedCard && (
                    <IconCheck
                      size="md"
                      isFilled={true}
                      className="tw-ml-auto tw-mr-2 tw-fill-gray-600"
                    />
                  )}
                </Link>
              );
            })}
        </div>
      </ModalBody>
    </Modal>
  );
};

export default SelectCardModal;
